import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { BottomBar, NavBtn, MoreSheet, MaestroMobileNav, SectionMobileNav, ADMIN_TABS } from "@/components/MaestroMobileNav";
import { useUrgentTickets, UrgentDot } from "@/lib/urgentTickets";
import { useSwitchRole } from "@/lib/useSwitchRole";
import { loginPath } from "@/lib/nextPath";
import { Compass, Ticket, Heart, User, LogIn, Award, CalendarDays, MoreHorizontal, Store, Users, Smartphone } from "lucide-react";
import { InstallAppDialog, isStandalone } from "@/components/InstallAppDialog";

const HIDDEN = ["/elegir-perfil"];
const OWN_NAV = ["/maestro", "/negocio", "/admin"];

export function AlumnoBottomNav() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  if (HIDDEN.includes(pathname) || OWN_NAV.includes(pathname)) return null;
  if (user?.role === "business") return <MaestroMobileNav value={null} onChange={(t) => navigate(`/maestro?tab=${t}`)} />;
  if (user?.role === "super_admin") {
    return <SectionMobileNav tabs={ADMIN_TABS} value={null} onChange={(t) => navigate(`/admin?tab=${t}`)} testId="admin-bottom-nav" />;
  }
  return <CustomerNav />;
}

function CustomerNav() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const switchRole = useSwitchRole();
  const [invites, setInvites] = useState(0);
  const [open, setOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const isCustomer = user?.role === "customer";
  useEffect(() => {
    if (!isCustomer) return setInvites(0);
    api.get("/credits/mine").then((r) => setInvites(r.data.invites.length)).catch(() => {});
  }, [isCustomer, pathname]);
  const urgent = useUrgentTickets(isCustomer, pathname);

  const go = (to) => {
    setOpen(false);
    navigate(to);
    if (to === "/explorar") setTimeout(() => document.getElementById("talleres")?.scrollIntoView({ behavior: "smooth" }), 80);
  };
  const main = [
    { to: "/explorar", label: "Explorar", icon: Compass, active: pathname === "/" || pathname === "/explorar", id: "explorar" },
    { to: "/mis-tickets", label: "Tickets", icon: Ticket, active: pathname === "/mis-tickets", id: "tickets", urgent },
    ...(isCustomer ? [{ to: "/mis-sesiones", label: "Sesiones", icon: CalendarDays, active: pathname === "/mis-sesiones", id: "sesiones" }] : []),
    ...(isCustomer ? [{ to: "/mis-maestros", label: "Maestros", icon: Users, active: pathname === "/mis-maestros", id: "maestros" }]
      : [{ to: "/mis-reservas", label: "Deseos", icon: Heart, active: pathname === "/mis-reservas", id: "deseos" }]),
    ...(!user ? [{ to: loginPath(pathname), label: "Ingresar", icon: LogIn, active: false, id: "login" }] : []),
  ];
  const more = [
    { key: "deseos", label: "Lista de Deseos", icon: Heart, iconClass: "text-coral", active: pathname === "/mis-reservas", onClick: () => go("/mis-reservas"), testId: "anav-deseos" },
    { key: "creditos", label: "Mis créditos", icon: Award, iconClass: "text-sun", active: pathname === "/mis-creditos", badge: invites, onClick: () => go("/mis-creditos"), testId: "anav-creditos" },
    { key: "perfil", label: "Mi Perfil", icon: User, active: pathname === "/mi-perfil", onClick: () => go("/mi-perfil"), testId: "anav-perfil" },
    ...(isStandalone() ? [] : [{ key: "install", label: "Instalar App", icon: Smartphone, iconClass: "text-primary", active: false, onClick: () => { setOpen(false); setInstallOpen(true); }, testId: "anav-install-app" }]),
  ];
  const actions = user?.can_switch_role
    ? [{ key: "switch", label: "Perfil Maestro", icon: Store, onClick: () => { setOpen(false); switchRole("business"); }, testId: "anav-switch-maestro" }]
    : [];
  const inMore = more.some((m) => m.active);

  return (
    <>
      <BottomBar testId="alumno-bottom-nav">
        {main.map((it) => (
          <div key={it.id} className="relative flex-1 min-w-0 flex">
            <NavBtn active={it.active} icon={it.icon} label={it.label} onClick={() => go(it.to)} testId={`anav-${it.id}`} />
            <UrgentDot n={it.urgent} testId="anav-urgent-badge" className="absolute top-1 left-1/2 ml-2" />
          </div>
        ))}
        {isCustomer && (
          <div className="relative flex-1 min-w-0 flex">
            <NavBtn active={inMore || open} icon={MoreHorizontal} label="Más" onClick={() => setOpen(true)} testId="anav-more" />
            {invites > 0 && <span className="absolute top-1 left-1/2 ml-2 w-3 h-3 rounded-full bg-coral ring-2 ring-background" data-testid="anav-invites-badge" />}
          </div>
        )}
      </BottomBar>
      {isCustomer && (
        <MoreSheet open={open} onOpenChange={setOpen} groups={[
          { title: "Mi cuenta", items: more },
          { title: "Cambiar perfil", items: actions },
        ]} />
      )}
      {isCustomer && <InstallAppDialog open={installOpen} onOpenChange={setInstallOpen} />}
    </>
  );
}
